import { AuthStore, createAuthProvider } from '@/features/iam/auth/AuthStore'
import Session from '@/features/iam/auth/Session'
import { IamApi } from '@/features/iam/api/iam-api'
import env from '@/infra/env'
import { User } from '@/features/iam/users/User'
import { type Id } from '@/infra/id'
import { UserStore } from '@/features/iam/users/UserStore'
import { WorkspaceStore } from '@/features/iam/workspaces/WorkspaceStore'
import { DefaultPreselectedWorkspaceProvider } from '@/features/iam/workspaces/PreselectedWorkspaceProvider'
import { getThemeFromWorkspaceColors } from '@/features/iam/branding/workspace-theme'
import { distinctUntilChanged, filter, map, merge, of } from 'rxjs'
import { WorkspaceMemberStore } from '@/features/iam/members/WorkspaceMemberStore'
import { UserDataStore } from '@/features/iam/users/UserDataStore'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { pageActivityStream } from '@/infra/page-activity'
import { type WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import { lastSeenSync } from '@/features/iam/members/last-seen-sync'
import { MultiFactorAuthStore } from '@/features/iam/auth/MultiFactorAuthStore'
import { filterNonNil } from '@/utils/rx-util'
import type { Storage } from '@/infra/storage'
import type { OnlineStatusForUserFn } from '@/features/messages'
import { createAuthResolutionWaiter } from '@/features/iam/auth/auth-resolution'
import { TranslationStore } from '@/features/iam/translations/TranslationStore'
import { DefaultPreselectedWorkspaceTranslationsProvider } from '@/features/iam/translations/PreselectedWorkspaceTranslationsProvider'
import { getPreselectedWorkspaceThemeColors } from '@/features/iam/branding/preselected-workspace-theme'
import {
  createPreselectedWorkspaceInfoReader,
  type PreselectedWorkspaceInfo,
} from '@/features/iam/workspaces/preselected-workspace-info'
import { MagicLinkStore } from '@/features/iam/magic-link/MagicLinkStore'
import { RequestMagicLinkViewModel } from '@/features/iam/magic-link/RequestMagicLinkView/RequestMagicLinkViewModel'
import type { GenerateSelfServiceMagicLink } from '@/features/iam/magic-link/types'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import type {
  GetExternalUrlDelegate,
  GetLifecycleDelegate,
} from '@/features/shell/platform/types'

/**
 * Props used to prime a user in the store with data to use before the user
 * has been fully loaded.
 */
export interface PrimeUserProps {
  id: Id
  givenName?: string
  familyName?: string
  avatarUrl?: string
}

/**
 * Delegate used for priming a user.
 */
export type PrimeUserFn = (props: PrimeUserProps) => User

/**
 * Props used to prime a workspace in the store.
 */
export interface PrimeWorkspaceProps {
  id: Id
}

/**
 * Delegate used for priming a workspace.
 */
export type PrimeWorkspaceFn = (props: PrimeWorkspaceProps) => Workspace

/**
 * Delegate for getting the currently logged-in user.
 * Should only be called after we're certain that the user is authenticated.
 */
export type GetCurrentUserFn = () => User

/**
 * Delegate for getting a user by ID.
 * Returns `null` if not found.
 */
export type GetUserFn = (userId: Id) => User | null

/**
 * Delegate for fetching a user by ID.
 */
export type FetchUserFn = (userId: Id) => Promise<User | null>

/**
 * Delegate for getting the currently selected workspace.
 * Should only be called after we're certain that the user is authenticated
 * and a workspace is selected.
 */
export type GetCurrentWorkspaceFn = () => Workspace

/**
 * Delegate for getting the currently logged-in member in the selected workspace.
 * Should only be called after we're certain that the user is authenticated
 * and a workspace is selected.
 */
export type GetCurrentMemberFn = () => WorkspaceMember

/**
 * Delegate for selecting a workspace.
 */
export type SelectWorkspaceByIdFn = (
  workspaceId: Id,
  signal?: AbortSignal,
) => Promise<void>

/**
 * Delegate for translating UI text.
 */
export type TranslateFn = (
  key: string,
  defaultValue: string,
  locale?: string,
) => string

/**
 * Props used to prime a workspace member in the store.
 */
export interface PrimeWorkspaceMemberProps {
  workspaceId: Id
  userId: Id
}

/**
 * Delegate used for priming a workspace member.
 */
export type PrimeWorkspaceMemberFn = (
  props: PrimeWorkspaceMemberProps,
) => WorkspaceMember

/**
 * Delegate that waits until the app has decided whether we're authenticated
 * or not, and if so, which workspace we're operating in.
 *
 * This is needed because React Router calls loaders in parallel; that means the
 * top-level authentication loader won't be completed before page-specific
 * loaders run, so we need to wait for the auth check to finish before
 * proceeding with any API calls.
 */
export type WaitForAuthResolutionFn = (
  signal?: AbortSignal,
) => Promise<AuthResolution>

/**
 * Authentication resolution from {@link WaitForAuthResolutionFn}.
 */
export interface AuthResolution {
  /**
   * Whether the user is authenticated.
   */
  authenticated: boolean

  /**
   * Which workspace has been selected, if any.
   * This is based on the current domain or session.
   * When using a non-workspace domain, this will be `null` if there's no
   * workspace selection saved from this session.
   */
  workspace: Workspace | null
}

/**
 * Delegate that reads the preselected workspace's info if there is one.
 */
export type ReadPreselectedWorkspaceInfoFn =
  () => PreselectedWorkspaceInfo | null

/**
 * Options for the IAM feature.
 */
export interface IamFeatureOptions {
  /**
   * Session storage access.
   */
  sessionStorage: Storage

  /**
   * Delegate for getting the lifecycle delegate.
   */
  getLifecycleDelegate: GetLifecycleDelegate

  /**
   * Delegate for getting the external URL delegate.
   */
  getExternalUrlDelegate: GetExternalUrlDelegate

  /**
   * The function to use for checking if a user is online.
   */
  isUserOnline: OnlineStatusForUserFn

  /**
   * Factory for creating snackbar error handlers.
   */
  createSnackbarErrorHandler: SnackbarErrorHandlerFactory
}

/**
 * Modules related to IAM.
 */
export type IamFeature = ReturnType<typeof IamFeature>

export function IamFeature({
  sessionStorage,
  isUserOnline,
  createSnackbarErrorHandler,
  getLifecycleDelegate,
  getExternalUrlDelegate,
}: IamFeatureOptions) {
  const readPreselectedWorkspaceInfo = createPreselectedWorkspaceInfoReader(
    DefaultPreselectedWorkspaceProvider.instance,
  )
  const authStore: AuthStore = new AuthStore({
    provider: createAuthProvider({
      sessionStorage,
      readPreselectedWorkspaceInfo,
      getLifecycleDelegate,
      getExternalUrlDelegate,
    }),
    authenticate: (tokenInfo, signal) =>
      session.authenticate(tokenInfo, signal),
    becomeActive$: pageActivityStream().pipe(
      map((a) => a.focused && a.visible),
      distinctUntilChanged(),
      filter(Boolean),
    ),
  })
  const token$ = authStore.token$
  const api = new IamApi({
    token$,
    baseURL: env.IAM_API,
  })

  const getUser: GetUserFn = (id: Id): User =>
    userStore.users.get(id) ?? User.none
  const workspaceMemberStore = new WorkspaceMemberStore(api, getUser, (props) =>
    primeWorkspace(props),
  )
  const primeWorkspaceMember: PrimeWorkspaceMemberFn =
    workspaceMemberStore.primeWorkspaceMember.bind(workspaceMemberStore)

  const userStore: UserStore = new UserStore(
    api,
    (user) => session.currentUser === user,
    isUserOnline,
    workspaceMemberStore.profileUpdated$,
  )

  const primeUser: PrimeUserFn = userStore.prime.bind(userStore)
  const getCurrentUser: GetCurrentUserFn = () => session.currentUser
  const fetchUser: FetchUserFn = (userId) => userStore.fetchUser(userId)

  const workspaceStore = new WorkspaceStore(api, getCurrentUser)
  const primeWorkspace: PrimeWorkspaceFn = (props) =>
    workspaceStore.primeWorkspace(props)
  const selectWorkspaceById: SelectWorkspaceByIdFn = (id, signal) =>
    session.selectWorkspaceById(id, signal)
  const multiFactorAuthStore = new MultiFactorAuthStore(api, getCurrentUser)
  const translate: TranslateFn = (key, defaultValue, locale) =>
    translationStore.translate(key, defaultValue, locale)

  const session = new Session(
    api,
    userStore,
    workspaceStore,
    workspaceMemberStore,
    DefaultPreselectedWorkspaceProvider.instance,
  )

  const userDataStore = new UserDataStore(session.currentMember$, api)

  // Run the last-seen sync.
  lastSeenSync(
    session.currentMember$,
    (member) => workspaceMemberStore.updateLastLogin(member.workspaceId),
    (member) => workspaceMemberStore.updateLastSeen(member.workspaceId),
  ).subscribe()

  const waitForAuthResolution: WaitForAuthResolutionFn =
    createAuthResolutionWaiter({
      currentWorkspace$: session.currentWorkspace$,
      isAuthenticated$: authStore.checkAuthenticationDecision$,
    })

  const translationStore = new TranslationStore(
    api.translations,
    DefaultPreselectedWorkspaceTranslationsProvider.instance,
    session.currentWorkspace$.pipe(filterNonNil()),
  )

  const magicLinkStore = new MagicLinkStore(api)

  const generateSelfServiceMagicLink: GenerateSelfServiceMagicLink = (
    workspaceId,
    destination,
  ) => magicLinkStore.generateSelfServiceMagicLink(workspaceId, destination)

  const requestMagicLinkVm = new RequestMagicLinkViewModel(
    magicLinkStore,
    createSnackbarErrorHandler,
  )

  return {
    // Internal
    authStore,
    workspaceStore,
    session,
    userStore,
    workspaceMemberStore,
    userDataStore,
    multiFactorAuthStore,
    translationStore,
    requestMagicLinkVm,

    // Public
    readPreselectedWorkspaceInfo,
    token$,
    checkAuthenticationDecision$: authStore.checkAuthenticationDecision$,
    waitForAuthResolution,
    primeWorkspace,
    getWorkspace: (workspaceId: Id) =>
      workspaceStore.workspaces.get(workspaceId) ?? Workspace.none,
    getCurrentWorkspace: () => session.currentWorkspace,
    fetchUser,
    primeUser,
    currentUser$: session.currentUser$,
    getCurrentUser: () => session.currentUser,
    getUser,
    currentUserData$: userDataStore.currentUserData$,
    currentMember$: session.currentMember$,
    currentWorkspace$: session.currentWorkspace$,
    logout: () => authStore.logout(),
    getCurrentMember: () => session.currentMember,
    primeWorkspaceMember,
    selectWorkspaceById,
    workspaceTheme$: merge(
      of(
        getPreselectedWorkspaceThemeColors(
          DefaultPreselectedWorkspaceProvider.instance,
        ),
      ),
      session.currentWorkspace$.pipe(map((w) => w?.branding.colors)),
    ).pipe(filterNonNil(), map(getThemeFromWorkspaceColors)),
    sessionOptions$: authStore.sessionOptions$,
    translate,
    logout$: authStore.logout$,
    generateSelfServiceMagicLink,
  }
}

/**
 * A composite ID for a workspace member.
 */
export interface WorkspaceMemberCompositeId {
  workspaceId: string
  userId: string
}

/**
 * Cache key function for a workspace member composite ID.
 *
 * @param id
 */
export function getWorkspaceMemberCompositeIdCacheKey(
  id: WorkspaceMemberCompositeId,
) {
  return `${id.workspaceId}/${id.userId}`
}

export type * from '@/features/iam/magic-link/types'
